import React from "react";
import { MAX_CLIENT_FIELDS, TIPOS_CAMPO, nuevoCampoCliente } from "./primerUso/salonPayload";
import "../clientes/clientes.css";

/* ─────────────────────────────────────────
   ClientFieldsEditor — definiciones de los campos propios de la ficha de cliente (solo dueño).
   Controlado: `fields` es la lista completa (archivados incluidos) y `onChange` recibe la lista nueva.
   El tipo de un campo ya guardado no se puede cambiar; el servidor nunca borra, solo archiva.
───────────────────────────────────────── */

const claveDe = (f, i) => f.id || f._key || `i${i}`;

const CampoCard = ({ campo, n, onPatch, onQuitar }) => {
  const guardado = Boolean(campo.id);
  return (
    <li className={"fc-field" + (campo.archived ? " fc-field--archived" : "")} aria-label={`Campo ${n}`}>
      <div className="fc-field__grid">
        <label className="zr-field">
          <span className="zr-field__label">Nombre del campo</span>
          <input
            className="zr-input"
            value={campo.label}
            maxLength={60}
            placeholder="Ej: Alergias, Color preferido"
            aria-label={`Nombre del campo ${n}`}
            onChange={(e) => onPatch({ label: e.target.value })}
          />
        </label>
        <label className="zr-field">
          <span className="zr-field__label">Tipo</span>
          <select
            className="zr-input"
            value={campo.type}
            disabled={guardado}
            aria-label={`Tipo del campo ${n}`}
            onChange={(e) => onPatch({ type: e.target.value, options: e.target.value === "select" ? campo.options : [] })}
          >
            {TIPOS_CAMPO.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          {guardado && <small className="fc-hint">El tipo no se puede cambiar. Si necesitás otro, archivá este campo y creá uno nuevo.</small>}
        </label>
      </div>

      {campo.type === "select" && (
        <label className="zr-field">
          <span className="zr-field__label">Opciones (una por línea, hasta 20)</span>
          <textarea
            className="zr-textarea fc-field__options"
            value={campo.options.join("\n")}
            aria-label={`Opciones del campo ${n}`}
            onChange={(e) => onPatch({ options: e.target.value.split("\n") })}
          />
        </label>
      )}

      <div className="fc-field__flags">
        <label className="fc-check">
          <input type="checkbox" checked={campo.askAtBooking} onChange={(e) => onPatch({ askAtBooking: e.target.checked })} />
          <span>Preguntar al reservar online</span>
        </label>
        <label className="fc-check">
          <input type="checkbox" checked={campo.required} onChange={(e) => onPatch({ required: e.target.checked })} />
          <span>Obligatorio al reservar</span>
        </label>
        {guardado ? (
          <button type="button" className="zr-btn zr-btn--soft fc-field__action" onClick={() => onPatch({ archived: !campo.archived })}>
            {campo.archived ? "Restaurar" : "Archivar"}
          </button>
        ) : (
          <button type="button" className="zr-btn zr-btn--ghost fc-field__action" onClick={onQuitar}>
            Quitar
          </button>
        )}
      </div>
      {campo.archived && <small className="fc-hint">Archivado: no se pide al reservar ni se muestra en la ficha. Los datos ya cargados se conservan.</small>}
    </li>
  );
};

const ClientFieldsEditor = ({ fields, onChange }) => {
  const lleno = fields.length >= MAX_CLIENT_FIELDS;
  const parche = (index, patch) => onChange(fields.map((f, i) => (i === index ? { ...f, ...patch } : f)));

  return (
    <section id="salon-sec-clientes">
      <div className="zr-panel">
        <div className="zr-section-head">
          <h2 className="zr-section-head__title">Campos de la ficha de cliente</h2>
          <p className="zr-section-head__hint">| Datos propios de tu salón: los completás en la ficha y podés pedirlos al reservar online.</p>
        </div>
        <p className="fc-hint" role="note">
          Si querés registrar alergias o datos de salud, creá un campo propio. Zoco no controla el consentimiento del cliente para esos datos: es responsabilidad del salón.
        </p>

        {fields.length === 0 ? (
          <div className="fc-empty">
            <p>Todavía no creaste campos propios.</p>
            <p>Agregá el primero, por ejemplo "Alergias" o "Color de pelo habitual".</p>
          </div>
        ) : (
          <ul className="fc-field-list">
            {fields.map((f, i) => (
              <CampoCard
                key={claveDe(f, i)}
                campo={f}
                n={i + 1}
                onPatch={(patch) => parche(i, patch)}
                onQuitar={() => onChange(fields.filter((_, n) => n !== i))}
              />
            ))}
          </ul>
        )}

        <div className="fc-field-footer">
          <span className="fc-hint">{fields.length} de {MAX_CLIENT_FIELDS} campos (los archivados cuentan)</span>
          <button
            type="button"
            className="zr-btn zr-btn--dark"
            disabled={lleno}
            onClick={() => onChange([...fields, nuevoCampoCliente()])}
          >
            Agregar campo
          </button>
        </div>
        {lleno && <p className="fc-hint" role="status">Llegaste al máximo de {MAX_CLIENT_FIELDS} campos. Quitá uno nuevo o reutilizá uno archivado.</p>}
      </div>
    </section>
  );
};

export default ClientFieldsEditor;
